import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { Container } from "../layout/Container.jsx";
import { BRAND } from "../../lib/brand.js";
// Importing the video routes it through Vite's asset pipeline so the
// emitted filename carries a content hash (e.g. hero-video-abc123.mp4).
// That gives us automatic cache-busting on every release — when the
// source file changes, the URL changes, so phones don't keep showing
// the stale cached copy. (Previously the file lived in /public/ and
// shipped as /hero-video.mp4 forever.)
import heroVideoSrc from "../../assets/hero-video.mp4";

/**
 * Hero video plays a trimmed window: starts at 8s, loops back to 8s
 * when it reaches 1:40 (100s). The `#t=8` fragment in the URL gives
 * the browser a head-start so the first painted frame is already at
 * the in-point; the JS handler below enforces the out-point and
 * restarts playback. Audio is muted (also required for mobile
 * autoplay) and `playsInline` keeps iOS from going full-screen.
 */
const HERO_VIDEO = `${heroVideoSrc}#t=8`;
const VIDEO_LOOP_START = 8; // seconds
const VIDEO_LOOP_END = 100; // seconds (1:40)

const HERO_TITLE_LEAD = "Build your forever home,";
const HERO_TITLE_ACCENT = "your way.";
const HERO_CTA_PRIMARY = { label: "Visit a display home", to: "/request-an-appointment" };
const HERO_CTA_SECONDARY = { label: "See our ranges", to: "/home-designs" };

export function Hero() {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const enforceStart = () => {
      if (v.currentTime < VIDEO_LOOP_START) v.currentTime = VIDEO_LOOP_START;
    };
    const onTimeUpdate = () => {
      if (v.currentTime >= VIDEO_LOOP_END) {
        v.currentTime = VIDEO_LOOP_START;
        // play() can reject if autoplay is blocked; muted + playsInline
        // satisfy mobile autoplay rules, but we still swallow rejections.
        v.play().catch(() => {});
      }
    };

    v.addEventListener("loadedmetadata", enforceStart);
    v.addEventListener("timeupdate", onTimeUpdate);
    if (v.readyState >= 1) enforceStart();

    return () => {
      v.removeEventListener("loadedmetadata", enforceStart);
      v.removeEventListener("timeupdate", onTimeUpdate);
    };
  }, []);

  return (
    <section className="relative isolate -mt-16 lg:-mt-20 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <video
          ref={videoRef}
          src={HERO_VIDEO}
          autoPlay
          muted
          playsInline
          preload="metadata"
          aria-hidden
          className="h-full w-full object-cover object-[center_55%]"
        />
        {/* Stronger bottom darken on mobile keeps copy legible without
            forcing extra vertical space. */}
        <div className="absolute inset-0 bg-gradient-to-t from-bg-base via-bg-base/60 to-bg-base/20 sm:via-bg-base/55 sm:to-bg-base/15" />
        <div className="absolute inset-0 bg-gradient-to-r from-bg-base/60 via-bg-base/10 to-transparent" />
      </div>

      {/* Sized in CSS pixels on mobile (640px) so the hero stays consistent
          across phones regardless of viewport-height quirks; vh-based on
          tablet/desktop where there's real estate to fill. */}
      <Container className="flex min-h-[600px] flex-col justify-end pb-10 pt-20 sm:min-h-[80vh] sm:pb-20 sm:pt-32 lg:min-h-screen lg:pb-28 lg:pt-44">
        <div className="max-w-3xl">
          {/* Italic tagline hidden on small screens — mobile is busy enough
              already and the headline carries the same idea. */}
          <p className="hidden max-w-md font-display text-base italic text-accent/90 sm:block lg:text-lg">
            Where craft meets character.
          </p>

          <h1 className="mt-3 font-display text-[26px] leading-[1.1] text-text-primary sm:mt-3 sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl">
            {HERO_TITLE_LEAD}{" "}
            <span className="text-accent">{HERO_TITLE_ACCENT}</span>
          </h1>

          {/* Compact location chip on mobile — drops the preamble so the
              service areas alone are the focus. */}
          <p className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-accent/30 bg-bg-base/40 px-3 py-1 text-[11px] text-text-primary backdrop-blur sm:mt-5 sm:gap-2 sm:px-3.5 sm:py-1.5 sm:text-sm">
            <MapPin className="h-3 w-3 shrink-0 text-accent sm:h-3.5 sm:w-3.5" />
            <span className="font-medium text-accent">
              {BRAND.serviceAreas.join(" · ")}
            </span>
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-2.5 sm:mt-8 sm:gap-4 lg:mt-10">
            <Link
              to={HERO_CTA_PRIMARY.to}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-2.5 text-[13px] font-medium text-bg-base transition-all hover:bg-accent-hover hover:shadow-[0_12px_32px_-8px_rgba(212,175,55,0.6)] sm:gap-2 sm:px-7 sm:py-3.5 sm:text-base"
            >
              {HERO_CTA_PRIMARY.label}
              <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Link>
            <Link
              to={HERO_CTA_SECONDARY.to}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-base/40 px-4 py-2.5 text-[13px] text-text-primary backdrop-blur transition-colors hover:border-accent hover:text-accent sm:gap-2 sm:px-7 sm:py-3.5 sm:text-base"
            >
              {HERO_CTA_SECONDARY.label}
            </Link>
          </div>
        </div>
      </Container>

      <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-accent/60 to-transparent" />
    </section>
  );
}
